<template>
  <div class="shop-applicant" :class="{ 'shop-applicant--disabled': disabled }">
    <InputComponent :label="$tc('buyer', buyers.length)" class="shop-applicant__buyer" :required="!disabled">
      <template #default>
        <UserCard
          :value="buyer"
          v-for="buyer in buyers"
          :key="buyer.email"
          theme="list"
          class="elder-input__element"
          @click.native="show"
        />
        <UserCard
          v-if="!buyers.length"
          :value="{ name: $t('notset') }"
          theme="list"
          class="elder-input__element shop-applicant__buyer-placeholder"
          @click.native="show"
        />
      </template>
      <template #below>
        <slot name="below" />
        <span v-if="!validator.passes && hasData" class="text--error text--small mt-1">
          <FontAwesomeIcon icon="exclamation-circle" />
          <span style="margin-left: 5px">{{ $t('missingInformation') }}</span>
        </span>
      </template>
    </InputComponent>

    <Modal :show="Boolean(data)" @close="data = null">
      <Card theme="flat" style="width: 100%; max-width: 800px">
        <template #header>
          <h2>{{ $tc('buyer') }}</h2>
        </template>
        <template #default>
          <Applicant v-model="data" :rules="rulesExtension" />
        </template>
        <template #footer>
          <ButtonComponent :label="$t('cancel')" :icon="['fal', 'times']" @click="close" />
          <ButtonComponent
            :label="$t('confirm')"
            :icon="['fal', 'arrow-to-right']"
            :disabled="!validator.passes"
            theme="primary"
            @click="confirm"
          />
        </template>
      </Card>
    </Modal>
  </div>
</template>

<script>
import Validator from '@/mixins/validator'
import { Clone } from '@/utils'
import { ModalComponent as Modal } from 'vue-elder-modal'
import { InputComponent } from 'vue-elder-input'
import ModelFactory from '@/models/utils/factory'
import PersonModel from '@/models/ShopContract/person'
import Applicant, { rules as ApplicantRules, labels as ApplicantLabels } from '@/components/Contract/Applicant'
import Card from '@/components/Card'
import UserCard from '@/components/User/Card'

const Model = ModelFactory({
  applicant: PersonModel(),
  coApplicant: PersonModel(),
  hasCoApplicant: false,
})

const RulesExtension = {
  'applicant.socialSecurityNumber': `socialSecurityNumber|different:coApplicant.socialSecurityNumber`,
  'coApplicant.socialSecurityNumber': `socialSecurityNumber`,
}

export { RulesExtension }
export default {
  mixins: [Validator],
  props: {
    value: Object,
    disabled: Boolean,
  },
  data() {
    return {
      data: null,
      rulesExtension: RulesExtension,
      validator: {
        data: 'dataComp',
        rules: {
          ...ApplicantRules,
          ...RulesExtension,
        },
        labels: {
          ...ApplicantLabels,
        },
      },
    }
  },
  computed: {
    dataComp() {
      if (!this.data)
        return {
          ...(this.$path('value.data') || {}),
          hasCoApplicant: this.$path('value.data.hasCoApplicant') ? this.$t('yes') : this.$t('no'),
        }
      return {
        ...this.data,
        hasCoApplicant: this.data.hasCoApplicant ? this.$t('yes') : this.$t('no'),
      }
    },
    hasData() {
      return this.buyers
        .map(buyer => Object.values(buyer))
        .flat()
        .some(Boolean)
    },
    buyers() {
      let value = []

      if (this.$path('value.data.applicant.name')) value.push(this.value.data.applicant)
      if (this.$path('value.data.hasCoApplicant') && this.$path('value.data.coApplicant.name'))
        value.push(this.value.data.coApplicant)

      return value
    },
  },
  methods: {
    show() {
      if (this.disabled) return
      this.data = Model(this.value.data)
    },
    close() {
      this.data = null
    },
    confirm() {
      let data = Clone(this.data)
      if (!data.hasCoApplicant) data.coApplicant = null
      this.value.data = { ...this.value.data, ...data }
      this.close()
    },
  },
  components: {
    Modal,
    Applicant,
    Card,
    UserCard,
    InputComponent,
  },
}
</script>

<style lang="scss">
.shop-applicant {
  &--disabled {
    .shop-applicant__buyer .elder-input__element {
      cursor: default;
      pointer-events: none;
      background-color: rgba(black, 0.03);
    }
  }
  &__buyer {
    &-placeholder {
      color: rgba(black, 0.5);
      cursor: pointer;
      padding: 1.4em 1.1em;

      .card__header {
        display: none;
      }
    }

    .elder-input {
      &__value {
        display: grid;
        grid-gap: 0.5rem;
      }
      &__element {
        cursor: pointer;
      }
    }

    .user-card + .user-card {
      border-top: 1px solid var(--border-color);
      border-radius: 0 0 var(--border-radius) var(--border-radius);
    }
  }
}
</style>
